import { useEffect, useRef, useState } from 'react'

import { DownloadCSVButton } from 'components/ui/download-csv'
import { TransactionsHistory, headerCSVRows } from 'components/ui/transaction-history'
import { TransactionMain } from 'components/ui/transaction-history/components/transaction'

import { useAppDispatch, useAppSelector } from 'store/hooks'
import { selectedUser } from 'store/reducers/users'

import { saveAs } from 'file-saver'

import { UTCFormatDate } from 'components/ui/transaction-history/components/transaction/utils'

import { exchangesSaveCSV } from 'pages/account/exchange/exchange/components/transaction-tab/utils'

import { useTranslation } from 'utils/use-translation'

import { fetchTransactions } from '../../../../store/fetchers/users'

import {
  TransactionTypeView,
  TransactionStatusView,
} from '../../../../components/ui/transaction-history/components/transaction/enum'

import {
  ETransactionTypes,
  TransactionAccountType,
  ETransactionStatuses,
  ITransaction,
} from '../../../../store/fetchers/users/types'

import { IAmountAndCoin } from '../../wallets/info/components/tab-transactions'

import { TabNames, TransactionHistoryTabs } from './tabs'

import styles from './styles.module.scss'
import { ITransactionTabProps } from './transactions-tab.props'
import { setAllTransactions } from 'store/reducers/wallets'

type TabName = typeof TabNames[keyof typeof TabNames]

export const matchType = (type: ETransactionTypes, accountType: TransactionAccountType): TransactionTypeView => {
  if (type === ETransactionTypes.DEPOSIT && (accountType === 'loan' || accountType === 'saving')) {
    return TransactionTypeView.DEPOSIT
  }

  if (type === ETransactionTypes.DEPOSIT && accountType === 'wallet') {
    return TransactionTypeView.TOP_UP
  }

  if (type === ETransactionTypes.REPAYMENT) {
    return TransactionTypeView.REPAYMENT
  }

  if (type === ETransactionTypes.INTEREST) {
    return TransactionTypeView.INTEREST
  }

  if (type === ETransactionTypes.WITHDRAW && accountType === 'loan') {
    return TransactionTypeView.LOAN
  }

  if (type === ETransactionTypes.WITHDRAW && (accountType === 'wallet' || accountType === 'saving')) {
    return TransactionTypeView.WITHDRAWAL
  }

  if (type === ETransactionTypes.PLEDGE) {
    return TransactionTypeView.REDEMPTION
  }

  if (type === ETransactionTypes.EXCHANGE) {
    return TransactionTypeView.EXCHANGE
  }

  if (type === ETransactionTypes.COLLATERAL) {
    return TransactionTypeView.COLLATERAL
  }

  if (type === ETransactionTypes.LOAN) {
    return TransactionTypeView.LOAN
  }

  if (type === ETransactionTypes.MONTHLY_INTEREST || type === ETransactionTypes.HOURLY_INTEREST) {
    return TransactionTypeView.MONTHLY_INTEREST
  }

  return null
}

export const matchStatus = (status: ETransactionStatuses): TransactionStatusView => {
  switch (status?.toLowerCase()) {
    case ETransactionStatuses.PENDING:
      return TransactionStatusView.PENDING
    case ETransactionStatuses.CONFIRMING:
      return TransactionStatusView.CONFIRMING
    case ETransactionStatuses.PROCESSING:
    case ETransactionStatuses.REJECTED:
      return TransactionStatusView.PROCESSING
    case ETransactionStatuses.FINISHED:
      return TransactionStatusView.SUCCESS
    case ETransactionStatuses.REFUNDED:
      return TransactionStatusView.REFUNDED
    default:
      return null
  }
}

export const matchAmountAndCoin = (tx: ITransaction): IAmountAndCoin => {
  if (tx.transaction_type === ETransactionTypes.DEPOSIT && tx.account_type !== 'saving') {
    return {
      amount: tx.to_amount ?? tx.from_amount,
      code: tx.to_currency,
      network: tx.to_network,
      incoming: true,
    }
  }

  if (tx.transaction_type === ETransactionTypes.DEPOSIT && tx.account_type === 'saving') {
    return {
      amount: tx.from_amount,
      code: tx.from_currency,
      network: tx.from_network,
      incoming: true,
    }
  }

  if (
    tx.transaction_type === ETransactionTypes.REPAYMENT &&
    (tx.account_type === 'loan' || tx.account_type === 'saving')
  ) {
    return {
      amount: tx.to_amount,
      code: tx.to_currency,
      network: tx.to_network,
      incoming: false,
    }
  }

  if (tx.transaction_type === ETransactionTypes.REPAYMENT && tx.account_type === 'wallet') {
    return {
      amount: tx.from_amount,
      code: tx.from_currency,
      network: tx.from_network,
      incoming: false,
    }
  }

  if (tx.transaction_type === ETransactionTypes.INTEREST && tx.account_type === 'loan') {
    return {
      amount: tx.to_amount,
      code: tx.to_currency,
      network: tx.to_network,
      incoming: false,
    }
  }

  if (tx.transaction_type === ETransactionTypes.INTEREST && tx.account_type === 'saving') {
    return {
      amount: tx.to_amount,
      code: tx.to_currency,
      network: tx.to_network,
      incoming: true,
    }
  }

  if (tx.transaction_type === ETransactionTypes.WITHDRAW && tx.account_type === 'loan') {
    return {
      amount: tx.to_amount,
      code: tx.to_currency,
      network: tx.to_network,
      incoming: true,
    }
  }

  if (tx.transaction_type === ETransactionTypes.WITHDRAW && tx.account_type === 'wallet') {
    return {
      amount: tx.from_amount,
      code: tx.to_currency,
      network: tx.to_network,
      incoming: true,
    }
  }

  if (tx.transaction_type === ETransactionTypes.INTEREST && tx.account_type === 'saving') {
    return {
      amount: tx.to_amount,
      code: tx.to_currency,
      network: tx.to_network,
      incoming: false,
    }
  }

  if (tx.transaction_type === ETransactionTypes.PLEDGE) {
    return {
      amount: tx.to_amount,
      code: tx.to_currency,
      network: tx.to_network,
      incoming: true,
    }
  }

  return {
    amount: tx.from_amount,
    code: tx.from_currency,
    network: tx.from_network,
    incoming: true,
  }
}

function filterUniqueTransactions(transactions: TransactionMain[]): TransactionMain[] {
  const uniqueTransactionsMap = new Map<string, TransactionMain>()

  transactions.forEach((item) => {
    const key = `${item.id}-${item.type}`

    if (!uniqueTransactionsMap.has(key)) {
      uniqueTransactionsMap.set(key, item)
    }
  })

  return Array.from(uniqueTransactionsMap.values())
}

export const filterByTab = (list: TransactionMain[], tab: TabName): TransactionMain[] => {
  list = filterUniqueTransactions(list)
  if (tab !== TabNames.ALL) {
    list = list.filter((transaction) => {
      switch (tab) {
        case TabNames.LOANS:
          return transaction.accountType.includes('loan')
        case TabNames.SAVINGS:
          return transaction.accountType.includes('saving')
        case TabNames.EXCHANGE:
          return transaction.accountType.includes('wallet') && transaction.type.includes(TransactionTypeView.EXCHANGE)
        case TabNames.WALLETS:
          return transaction.accountType.includes('wallet')
        default:
          return true
      }
    })
  }
  return list
}

export function TransactionTab({ transactions, setTransactions, isLoading, setIsLoading }: ITransactionTabProps) {
  const { t } = useTranslation()

  const user = useAppSelector(selectedUser)
  const dispatch = useAppDispatch()

  const [activeTab, setActiveTab] = useState<TabName>(TabNames.ALL)
  const isFetched = useRef<boolean>(false)
  const [filtered, setFiltered] = useState<TransactionMain[]>([])
  const intervalRef = useRef(null)
  const [search, setSearch] = useState<string>('')

  useEffect(() => {
    if (user && !isFetched.current) {
      // Вычисляем количество миллисекунд до ближайших 30 секунд
      const now = new Date()
      const delay = 30000 - (now.getTime() % 30000)

      const initialTimeout = setTimeout(() => {
        intervalRef.current = setInterval(fetchAllTransactions, 30000)
      }, delay)

      return () => {
        clearTimeout(initialTimeout)
        if (intervalRef.current) {
          clearInterval(intervalRef.current)
        }
      }
    }
    return undefined
  }, [user])

  const fetchAllTransactions = async () => {
    const res = await fetchTransactions({ search })

    if (res) {
      dispatch(setAllTransactions(res))
      setTransactions(res)
    }
    
    setIsLoading(false)
  }

  const updatedList: TransactionMain[] = transactions
    ?.map((tx) => {
      const type = matchType(tx.transaction_type, tx.account_type)
      const status = matchStatus(tx.status)

      const amountAndCoin = matchAmountAndCoin(tx)

      if (
        type === TransactionTypeView.EXCHANGE ||
        tx.from_currency !== tx.to_currency ||
        tx.from_network !== tx.to_network
      ) {
        return {
          code: amountAndCoin.code,
          network: amountAndCoin.network,
          amount: amountAndCoin.amount,
          type: type,
          status: status,
          hash: tx.hash,
          label: type,
          date: tx.created_at,
          incoming: amountAndCoin.incoming,
          from_currency: {
            code: tx.from_currency,
            network: tx.from_network,
          },
          to_currency: {
            code: tx.to_currency,
            network: tx.to_network,
          },
          amountFrom: tx.from_amount,
          amountTo: tx.to_amount,
          accountType: tx.account_type,
          id: tx.transaction_id.toString(),
        }
      } else
        return {
          amount: amountAndCoin.amount,
          code: amountAndCoin.code,
          network: amountAndCoin.network,
          type: type,
          status: status,
          hash: tx.hash,
          label: type,
          date: tx.created_at,
          incoming: amountAndCoin.incoming,
          accountType: tx.account_type,
          id: tx.transaction_id.toString(),
        }
    })
    ?.filter((tx) => tx !== null && tx?.status !== null && tx?.type !== null)
    ?.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())

  useEffect(() => {
    if (updatedList) {
      setFiltered(filterByTab(updatedList, activeTab))
    }
  }, [activeTab, transactions])

  const isExchangeTab = activeTab === TabNames.EXCHANGE
  const fileNameCSV = `CoinRabbit_${activeTab}_transaction_history.csv`

  const saveCSV = () => {
    const rows = []

    filtered
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      .forEach((data) => {
        const { date, type, amount, code, network, hash, status } = data
        const res = [UTCFormatDate(date), type, amount, code, network, hash, status].join(',') + '\r\n'

        rows.push(res)
      })
    saveAs(new Blob([headerCSVRows, ...rows], { type: 'text/csv;charset=utf-8' }), fileNameCSV)
  }

  const handleTabChange = (tab) => {
    setActiveTab(tab)
  }

  const handleSearchClick = () => {
    fetchAllTransactions()
  }

  return (
    <>
      <div className={styles.header}>
        <h2>{t('transaction_history')}</h2>
        <DownloadCSVButton className={styles.downloadCSVOnlyMobile} onClick={saveCSV} />
      </div>
      <TransactionHistoryTabs activeTab={activeTab} onTabChange={handleTabChange} />

      <TransactionsHistory
        search={search}
        setSearch={setSearch}
        ownHeader
        isHideTypeFilter={isExchangeTab}
        {...(isExchangeTab && { saveCSVProp: () => exchangesSaveCSV(filtered, fileNameCSV) })}
        list={filtered}
        fileNameCSV={fileNameCSV}
        isDashboard
        onSearchClick={handleSearchClick}
        isLoading={isLoading}
      />
    </>
  )
}
